import { useState } from "react";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiErrorMessage } from "../api/client";

export function LoginPage() {
  const { login, submitSessionInitials, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<"credentials" | "initials">("credentials");

  if (user) {
    const from = (location.state as { from?: Location })?.from?.pathname ?? "/";
    return <Navigate to={from} replace />;
  }

  function goToOriginalDestination() {
    const from = (location.state as { from?: Location })?.from?.pathname ?? "/";
    navigate(from, { replace: true });
  }

  async function onFinish(values: { email: string; password: string }) {
    setError(null);
    setSubmitting(true);
    try {
      const { requiresInitials } = await login(values.email, values.password);
      if (requiresInitials) {
        setStep("initials");
      } else {
        goToOriginalDestination();
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Invalid email/username or password"));
    } finally {
      setSubmitting(false);
    }
  }

  async function onFinishInitials(values: { initials: string }) {
    setError(null);
    setSubmitting(true);
    try {
      await submitSessionInitials(values.initials);
      goToOriginalDestination();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save your initials"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0f2f5" }}>
      <Card style={{ width: 360 }}>
        <Typography.Title level={3} style={{ textAlign: "center", marginTop: 0 }}>
          PantrySystems 2.0
        </Typography.Title>
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        {step === "credentials" ? (
          <Form layout="vertical" onFinish={onFinish} disabled={submitting}>
           <Form.Item
              name="email"
              label="Email or Username"
              rules={[{ required: true, message: "Enter your email or username" }]}
            >
              <Input autoFocus autoComplete="username" />
            </Form.Item>
            <Form.Item name="password" label="Password" rules={[{ required: true }]}>
              <Input.Password autoComplete="current-password" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" block loading={submitting}>
                Log in
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form layout="vertical" onFinish={onFinishInitials} disabled={submitting}>
            <Typography.Paragraph type="secondary">
              This pantry tracks who's working on a shared login. Enter your initials to continue.
            </Typography.Paragraph>
            <Form.Item
              name="initials"
              label="Your initials"
              rules={[
                { required: true, message: "Enter your initials" },
                { pattern: /^[A-Za-z]{1,4}$/, message: "Up to 4 letters" },
              ]}
            >
              <Input autoFocus maxLength={4} autoComplete="off" style={{ textTransform: "uppercase" }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" block loading={submitting}>
                Continue
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
}